import _ from 'lodash'

import i18n from '@/i18n/i18n'
import SalesChannelService from '@/services/admin/SalesChannelService.js'
import { build_query } from '@/utilities/helpers.js'

import { SALES_CHANNEL_CONFIG } from '@/constants/SalesChannelConfig'

const { t } = i18n.global

export const namespaced = true

export const state = {
  salesChannels: null,
  salesChannel: {},
  products: null,
  unselectedProducts: null,
  productsMeta: null,
  workflows: null,
  workflow: {}
}

export const allowedFilters = ['name']

export const mutations = {
  SET_SALES_CHANNELS(state, salesChannels) {
    state.salesChannels = salesChannels.data
  },
  SET_SALES_CHANNEL(state, salesChannel) {
    state.salesChannel = salesChannel
  },
  SET_PRODUCTS(state, products) {
    state.products = products.data
    state.productsMeta = products.meta
  },
  SET_UNSELECTED_PRODUCTS(state, products) {
    state.unselectedProducts = _.filter(products.all, function (product) {
      return _.findIndex(products.sales_channel, { id: product.id }) === -1
    })
  },
  REMOVE_PRODUCT(state, productId) {
    state.products = state.products.filter(product => product.id !== productId)
  },
  SET_WORKFLOWS(state, workflows) {
    state.workflows = workflows.data
  },
  SET_WORKFLOW(state, workflow) {
    state.workflow = workflow.data
  },
  SET_SORTED_WORKFLOWS(state, workflows) {
    workflows.map((value, index) => {
      workflows[index].order = index
    })
    state.workflows = workflows
  }
}

export const getters = {
  getSalesChannelById: state => id => {
    return state.salesChannels ? state.salesChannels.find(channel => channel.id === id) : false
  }
}

export const actions = {
  getSalesChannels({ commit, dispatch, state }) {
    if (state.salesChannels) return state.salesChannels

    return new Promise((resolve, reject) => {
      SalesChannelService.getSalesChannels()
        .then(response => {
          commit('SET_SALES_CHANNELS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.sales_channel.sales_channels')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getSalesChannelsWithNull({ commit, dispatch, state }) {
    if (state.salesChannels) return state.salesChannels

    return new Promise((resolve, reject) => {
      SalesChannelService.getSalesChannels()
        .then(response => {
          response.data.push({ id: 0, name: 'Boş' })
          commit('SET_SALES_CHANNELS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.sales_channel.sales_channels')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getReportableSalesChannels({ commit, dispatch }) {
    return new Promise((resolve, reject) => {
      let salesChannelQuery = build_query(
        {
          'filter[byConfig]': SALES_CHANNEL_CONFIG.REPORTABLE
        },
        ['byConfig']
      )
      SalesChannelService.getSalesChannelsByConfig(salesChannelQuery)
        .then(response => {
          commit('SET_SALES_CHANNELS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.sales_channel.sales_channels')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getSalesChannel({ commit, state, getters }, id) {
    if (id == state.salesChannel.id) return state.salesChannel

    let salesChannel = getters.getSalesChannelById(id)

    if (salesChannel) {
      commit('SET_SALES_CHANNEL', salesChannel)
      return salesChannel
    } else {
      return new Promise(resolve => {
        SalesChannelService.getSalesChannel(id).then(response => {
          commit('SET_SALES_CHANNEL', response.data)
          resolve(response.data)
        })
      })
    }
  },
  getProducts({ commit, dispatch, rootState }, params) {
    let newQuery = build_query(params.query, allowedFilters)

    return new Promise((resolve, reject) => {
      SalesChannelService.getProducts(params.sales_channel_id, newQuery)
        .then(response => {
          commit('SET_PRODUCTS', response)
          commit('SET_UNSELECTED_PRODUCTS', {
            sales_channel: response.data,
            all: rootState.adminProduct.products
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              elements: t('store.admin.sub_category.products')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  attachProducts({ dispatch, state }, products) {
    return new Promise((resolve, reject) => {
      SalesChannelService.attachProducts(state.salesChannel.id, products)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.added', {
              element: t('store.admin.sub_category.products')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.adding_problem', {
              element: t('store.admin.sub_category.products')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  detachProducts({ dispatch, commit, state }, product) {
    return new Promise((resolve, reject) => {
      SalesChannelService.detachProducts(state.salesChannel.id, product.id)
        .then(response => {
          commit('REMOVE_PRODUCT', product.id)
          const notification = {
            type: 'success',
            message: t('store.shared.removed', {
              element: t('store.admin.sub_category.products')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.remove_error', {
              element: t('store.admin.sub_category.products')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getWorkflows({ commit, dispatch, state }) {
    return new Promise((resolve, reject) => {
      SalesChannelService.getWorkflows(state.salesChannel.id)
        .then(response => {
          commit('SET_WORKFLOWS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.workflow.order_workflows')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  getWorkflow({ commit, dispatch, state }, workflow_job_id) {
    return new Promise((resolve, reject) => {
      SalesChannelService.getWorkflow(state.salesChannel.id, workflow_job_id)
        .then(response => {
          commit('SET_WORKFLOW', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.fetching_problem', {
              element: t('store.admin.workflow.order_workflows')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  attachWorkflow({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      SalesChannelService.attachWorkflow(state.salesChannel.id, params)
        .then(response => {
          commit('SET_WORKFLOWS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.adding_problem', {
              element: t('store.admin.workflow.order_workflow')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  detachWorkflow({ commit, dispatch, state }, workflow) {
    return new Promise((resolve, reject) => {
      SalesChannelService.detachWorkflow(state.salesChannel.id, workflow)
        .then(response => {
          commit('SET_WORKFLOWS', response)
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.remove_error', {
              element: t('store.admin.workflow.order_workflow')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  updateWorkflow({ commit, dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      SalesChannelService.updateWorkflow(state.salesChannel.id, params)
        .then(response => {
          commit('SET_WORKFLOWS', response)
          resolve(response.data)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.update_error', {
              element: t('store.admin.workflow.order_workflow')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  sortWorkflows({ dispatch, state }, params) {
    return new Promise((resolve, reject) => {
      SalesChannelService.sortWorkflows(state.salesChannel.id, params)
        .then(response => {
          const notification = {
            type: 'success',
            message: t('store.shared.updated', {
              element: t('store.admin.workflow.order_workflow')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          resolve(response)
        })
        .catch(err => {
          const notification = {
            type: 'danger',
            message: t('store.shared.update_error', {
              element: t('store.admin.sales_channel.order_workflow_order')
            })
          }
          dispatch('notification/add', notification, {
            root: true
          })
          reject(err)
        })
    })
  },
  setWorkflows({ commit }, workflows) {
    commit('SET_SORTED_WORKFLOWS', workflows)
  }
}
